import {
	type CurrencyLightCurrencyNetwork,
	Decimal,
	type GetBalanceCurrencyDTO,
	type Image,
	useFormatter,
	type WalletCurrency
} from '@capsicum/common';

import { usePartners } from '../../partners';
import { useMainCurrenciesQuery } from '../../wallet/data-access/queries/useMainCurrenciesQuery';
import { type NCWTokenBalanceType, type NCWTokenCardProps } from '../models';
import { roundDownNum } from '../util';

/**
 * This hook is for the Mob/Web useNCWBalance() hooks. It contains some shared code
 * between Mob/Web useNCWBalance() hooks.
 */
export const useBalanceHelper = ({ fiatCurrency }: { fiatCurrency?: CurrencyLightCurrencyNetwork } = {}) => {
	const { formatCrypto } = useFormatter();
	const { getPartnerBy } = usePartners();

	//retrieve the user's default and alternate currencies
	const { data: currencies, isFetching: isCurrencyFetching, isLoading: isCurrencyLoading } = useMainCurrenciesQuery();
	const defaultShortCode = currencies?.defaultCurrency?.shortCode ?? 'CHZ';
	const altShortCode = currencies?.altCurrency?.shortCode ?? 'EUR';

	//a util to convert data from type GetBalanceCurrencyDTO to type NCWTokenBalanceType
	const convertToTokenBalance = (
		data: GetBalanceCurrencyDTO & {
			currency: {
				currencyDetails?: WalletCurrency & {
					images: Array<Image>;
				};
				stakeOperationId?: string;
				type?: string;
			};
		}
	): NCWTokenBalanceType => {
		const isVoteToken = data.currency.type === 'voteToken';
		const isExternalToken = data.currency.type === 'externalToken' && !data.currency.isCommunityToken;
		// Fan tokens amount is integer. e.g. 8 ACM; The other tokens amount (CHZ, external tokens) are decimal. e.g. 8.88 CHZ;
		const tokenAmount =
			isVoteToken && new Decimal(data.totalAmount).isInteger()
				? parseInt(data.totalAmount)
				: roundDownNum(data.totalAmount);
		const tokenUsableAmount =
			isVoteToken && new Decimal(data.usableAmount).isInteger()
				? parseInt(data.usableAmount)
				: roundDownNum(data.usableAmount);

		const amountRaw = new Decimal(data.totalAmount).toFixed().toString();
		const usableAmountRaw = new Decimal(data.usableAmount).toFixed().toString();

		const currencyLogo = data.currency.currencyDetails?.images?.find(
			image => image.imageType.type === 'currency_logo'
		);
		const partner = getPartnerBy(p => p.currencyId === data.currency.id);
		const title = partner?.name ?? data.currency.shortCode;

		const conversionToFiat =
			data.conversionRateFiat && fiatCurrency
				? {
						conversionRate: data.conversionRateFiat,
						fiatShortCode: fiatCurrency.shortCode
					}
				: undefined;
		const conversionToChz = data.conversionRateChz
			? {
					conversionRate: data.conversionRateChz,
					shortCode: 'CHZ'
				}
			: undefined;

		return {
			amount: tokenAmount,
			amountRaw,
			conversionToChz,
			conversionToFiat,
			currencyId: data.currency.id,
			decimalDetails: data.decimalDetails,
			isExternalToken: isExternalToken,
			shortCode: data.currency.shortCode,
			smartContractAddress: data.currencySmartContract,
			stakedAmount: data.stakedAmount,
			stakeOperationId: data.currency?.stakeOperationId,
			symbol: data.currency.symbol,
			tokenCardData: {
				formattedAltBalance: formatCrypto(data.totalAmountFiat, altShortCode),
				formattedDefaultBalance: formatCrypto(data.totalAmountChz ?? 0, defaultShortCode),
				partnerName: partner?.name,
				testID: `ncwToken-${data.currency.shortCode}-card`,
				title,
				tokenBalance: formatCrypto(tokenAmount, data.currency.shortCode),
				tokenBalanceRaw: `${amountRaw} ${data.currency.shortCode}`,
				tokenImgBlurhash: currencyLogo?.blurHash,
				tokenImgUrl: currencyLogo?.url,
				usableFormattedAltBalance: formatCrypto(data.usableAmountFiat, altShortCode),
				usableFormattedDefaultBalance: formatCrypto(roundDownNum(data.usableAmountChz ?? 0), defaultShortCode),
				usableTokenBalance: formatCrypto(tokenUsableAmount, data.currency.shortCode),
				usableTokenBalanceRaw: `${usableAmountRaw} ${data.currency.shortCode}`,
				variant: 'token'
			} satisfies NCWTokenCardProps,
			type: data.currency.type,
			usableAmount: tokenUsableAmount,
			usableAmountRaw
		};
	};

	return {
		altShortCode,
		convertToTokenBalance,
		currencies,
		defaultShortCode,
		isCurrencyFetching,
		isCurrencyLoading
	};
};
